import { ContextMenuSelect, DataGrid } from '@turnpike/portal-data-grid';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useActionsInfiniteQuery, useActionStatusesQuery, useDeleteActionsMutation } from '../api';
import { filterByStatus } from '../model/utils/action';

import { ExpandedRowDetails } from './components/ExpandedRowDetails';
import { useTableLayoutColumns } from './TableLayout.columns';
import Styles from './TableLayout.module.scss';

import type { ActionModel } from '../model/types/action.model';

import { useAppSelector } from '@/app/hooks/useAppSelector';
import { EditIcon, DeleteIcon, ActionsIcon } from '@/components/Elements/Icons';
import { ConfirmDeleteMessage } from '@/components/Modals/ConfirmDeleteMessage';
import { ConfirmDeleteModal } from '@/components/Modals/ConfirmDeleteModal';
import { useUsersDictionary } from '@/features/Shared/users/model/hooks/useUsersDictionary';

export const TableLayout = () => {
	const [deleteItem, setDeleteItem] = useState<ActionModel | null>(null);
	const [deleteAction, { isLoading: isDeleting }] = useDeleteActionsMutation();
	const [searchParams] = useSearchParams();
	const status = searchParams.get('status') ?? 'all';
	const search = searchParams.get('filter') ?? '';
	const filter = useAppSelector((state) => state.filter);

	const { currentData, isLoading, isFetching, fetchNextPage } = useActionsInfiniteQuery({
		...filter,
		title: search,
	});

	const isLoadingData = isLoading || (!currentData && isFetching);
	const items = useMemo(
		() => currentData?.pages?.flatMap((page) => page.items) ?? [],
		[currentData]
	);

	const actionsStatusesQueryArg = useMemo(() => {
		const itemIdsPerOrganization = items.reduce(
			(acc, item) => {
				if (!acc[item.organizationId]) {
					acc[item.organizationId] = [];
				}
				acc[item.organizationId].push(item.id);
				return acc;
			},
			{} as Record<string, string[]>
		);
		return {
			organizations: Object.entries(itemIdsPerOrganization).map(([orgId, actionsIds]) => ({
				id: orgId,
				actionsIds,
			})),
		};
	}, [items]);

	const { data: actionStatuses } = useActionStatusesQuery(actionsStatusesQueryArg, {
		skip: items.length === 0,
	});

	const filteredItems = useMemo(() => {
		return filterByStatus(items, status);
	}, [items, status]);

	const { data: users } = useUsersDictionary({
		pageSize: 1000,
		organizationIds: filter.organizationIds,
	});

	const { columns } = useTableLayoutColumns({
		actionsStatuses: actionStatuses?.actionsStatusMap,
	});

	const renderDetailsRow = (row: ActionModel) => {
		return (
			<ExpandedRowDetails
				row={row}
				users={users ?? {}}
				actionsStatusMap={actionStatuses?.actionsStatusMap ?? {}}
			/>
		);
	};

	return (
		<div className={Styles.container}>
			<div className={Styles.gridContainer}>
				<DataGrid
					hasMore={!!currentData?.pages[currentData.pages.length - 1]?.moreResults}
					onEndReached={fetchNextPage}
					data={filteredItems}
					isLoading={isLoadingData}
					columns={columns}
					renderExpandedRow={renderDetailsRow}
					renderActions={(row) => {
						return (
							<ContextMenuSelect>
								<ContextMenuSelect.Option
									title="Edit"
									icon={<EditIcon />}
									onClick={() => console.log('edit')}
								/>
								<ContextMenuSelect.Option
									title="Delete"
									icon={<DeleteIcon />}
									onClick={() => setDeleteItem(row)}
								/>
							</ContextMenuSelect>
						);
					}}
				/>
				{deleteItem && (
					<ConfirmDeleteModal
						title="Do you want to delete this action?"
						isLoading={isDeleting}
						onCloseRequest={() => setDeleteItem(null)}
						onConfirm={async () => {
							await deleteAction(deleteItem);
							setDeleteItem(null);
						}}
					>
						<ConfirmDeleteMessage
							iconBackground="#FF9100"
							icon={<ActionsIcon />}
							message={deleteItem.title}
						/>
					</ConfirmDeleteModal>
				)}
			</div>
		</div>
	);
};
